// @ts-strict-ignore
import { IVideoPipeline, VIRTUAL_BACKGROUND_TYPE } from 'rainbow-web-sdk/services/virtualBackground/virtualBackground.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';

export class Canvas2DPipeline implements IVideoPipeline {
    private vbService: any;
    private logger: LoggerService = LoggerService.getInstance();
    private segmentationPixelCount: number;
    private tflite: any;
    private segmentationMaskCtx: CanvasRenderingContext2D;
    private segmentationMask: any;
    private outputCanvasCtx: CanvasRenderingContext2D;
    private segmentationMaskCanvas: HTMLCanvasElement;

    public maskFrameTimerWorker: Worker;
    public virtualImage: CanvasImageSource;
    public virtualVideo: CanvasImageSource;

    /**
     * @function create
     * @static
     * @public
     * @returns {Canvas2DPipeline} Initialization of the class
     */
    public static create(virtualBackgroundService: any): Canvas2DPipeline {
        return new Canvas2DPipeline(virtualBackgroundService);
    }

    private constructor(virtualBackgroundService: any) {
        this.vbService = virtualBackgroundService;
    }

    /**
     * @function init
     * @public
     * @async
     * @description Retrieve the image or video to be used as a background
     */
    public async init(): Promise<any> {
        try {
            this.tflite = this.vbService.tflite;
            this.segmentationPixelCount = this.vbService.segmentationPixelCount;
            this.segmentationMask = new ImageData(this.vbService.options.segmentationDimensions.width, this.vbService.options.segmentationDimensions.height);
            this.segmentationMaskCanvas = document.createElement('canvas');
            this.segmentationMaskCanvas.width = this.vbService.options.segmentationDimensions.width;
            this.segmentationMaskCanvas.height = this.vbService.options.segmentationDimensions.height;
            this.segmentationMaskCtx = this.segmentationMaskCanvas.getContext('2d');

            this.outputCanvasCtx = this.vbService.outputCanvasElement.getContext('2d');

            if (this.vbService.options.backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE) await this.loadImage();
            else if (this.vbService.options.backgroundType === VIRTUAL_BACKGROUND_TYPE.VIDEO) {
                this.virtualVideo = document.createElement('video');
                this.virtualVideo.crossOrigin = 'anonymous';
                this.virtualVideo.onplaying = () => { };
                this.virtualVideo.onerror = () => { Error(`[VirtualBackgroundCanvas2D] -- create -- failed to load video`); };
                this.virtualVideo.src = this.vbService.options.imgBg;
                this.virtualVideo.play();
                this.virtualVideo.autoplay = true;
                this.virtualVideo.muted = true;
                this.virtualVideo.loop = true;
            }
        }
        catch (error) {
            this.logger.error(`[VirtualBackgroundCanvas2D] startEffect -- Failure -- ${error.message}`);
            throw error;
        }

    }

    private loadImage(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.virtualImage = new Image();
            this.virtualImage.crossOrigin = 'anonymous';
            this.virtualImage.onload = () => resolve();
            this.virtualImage.onerror = () => reject(`[VirtualBackgroundCanvas2D] -- create -- failed to load image`);  
            this.virtualImage.src = this.vbService.options.imgBg;
        });
    }

    /**
     * @function render
     * @public
     * @async
     * @description Calls the different methods to create the virtual background
     */
    public async render() {
        try {
            this.runPreProcessing();
            this.runInference();
            this.runPostProcessing();
        }
        catch (error) {
            this.logger.error(`[VirtualBackgroudCanvas2D] render -- failure -- ${error.message}`);
        }
    }

    public updatePostProcessingConfig() { }

    /**
     * @function cleanUp
     * @public
     * @description deletes the canvas created for the mask
     */
    public cleanUp() {
        this.segmentationMaskCanvas.remove();
    }

    /****************************************************************************/
    /** PUBLIC STUFF                                                           **/
    /****************************************************************************/

    /**
     * @function runPreProcessing
     * @private
     * @description Resize the mask and send the data to tflite
     */
    private runPreProcessing() {
        this.segmentationMaskCtx.drawImage(this.vbService.videoElement, 0, 0, this.vbService.videoElement.width, this.vbService.videoElement.height, 0, 0, this.vbService.options.segmentationDimensions.width, this.vbService.options.segmentationDimensions.height);
        const imageData = this.segmentationMaskCtx.getImageData(0, 0, this.vbService.options.segmentationDimensions.width, this.vbService.options.segmentationDimensions.height);
        const inputMemoryOffset = this.tflite._getInputMemoryOffset() / 4;
        for (let i = 0; i < this.segmentationPixelCount; i++) {
            this.tflite.HEAPF32[inputMemoryOffset + (i * 3)] = imageData.data[i * 4] / 255;
            this.tflite.HEAPF32[inputMemoryOffset + (i * 3) + 1] = imageData.data[(i * 4) + 1] / 255;
            this.tflite.HEAPF32[inputMemoryOffset + (i * 3) + 2] = imageData.data[(i * 4) + 2] / 255;
        }
    }

    /**
     * @function runInference
     * @private
     * @description Recovery of the AI data and application of the softMax equiation to create the mask
     */
    private runInference() {
        this.tflite._runInference();
        const outputMemoryOffset = this.tflite._getOutputMemoryOffset() / 4;

        for (let i = 0; i < this.segmentationPixelCount; i++) {
            const background = this.tflite.HEAPF32[outputMemoryOffset + (i * 2)];
            const person = this.tflite.HEAPF32[outputMemoryOffset + (i * 2) + 1];
            const shift = Math.max(background, person);
            const backgroundExp = Math.exp(background - shift);
            const personExp = Math.exp(person - shift);

            // Sets only the alpha component of each pixel.
            this.segmentationMask.data[(i * 4) + 3] = (255 * personExp) / (backgroundExp + personExp);
        }
        this.segmentationMaskCtx.putImageData(this.segmentationMask, 0, 0);
    }

    /**
     * @function runPostProcessing
     * @private
     * @description Finalization of the virtual background
     */
    private runPostProcessing() {
        this.outputCanvasCtx.globalCompositeOperation = 'copy';

        // Smooth out the edges.
        this.outputCanvasCtx.filter = this.vbService.options.backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE || this.vbService.options.backgroundType === VIRTUAL_BACKGROUND_TYPE.VIDEO ? 'blur(4px)' : 'blur(8px)';
        this.outputCanvasCtx.drawImage(this.segmentationMaskCanvas, 0, 0, this.vbService.options.segmentationDimensions.width, this.vbService.options.segmentationDimensions.height, 0, 0, this.vbService.videoElement.width, this.vbService.videoElement.height);
        this.outputCanvasCtx.globalCompositeOperation = 'source-in';
        this.outputCanvasCtx.filter = 'none';

        // Draw the foreground video.
        this.outputCanvasCtx.drawImage(this.vbService.videoElement, 0, 0);

        // Draw the background.
        this.outputCanvasCtx.globalCompositeOperation = 'destination-over';
        if (this.vbService.options.backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE) {
            this.outputCanvasCtx.drawImage(this.virtualImage, 0, 0, this.vbService.videoElement.width, this.vbService.videoElement.height);
        }
        else if (this.vbService.options.backgroundType === VIRTUAL_BACKGROUND_TYPE.VIDEO) {
            this.outputCanvasCtx.drawImage(this.virtualVideo, 0, 0, this.vbService.videoElement.width, this.vbService.videoElement.height);
        }
        else if (this.vbService.options.backgroundType === VIRTUAL_BACKGROUND_TYPE.BLUR) {
            this.outputCanvasCtx.filter = `blur(6px)`;
            this.outputCanvasCtx.drawImage(this.vbService.videoElement, 0, 0);
        }
        else if (this.vbService.options.backgroundType === VIRTUAL_BACKGROUND_TYPE.BLUR_PLUS) {
            this.outputCanvasCtx.filter = `blur(30px)`;
            this.outputCanvasCtx.drawImage(this.vbService.videoElement, 0, 0);
        }
    }


}
